import { setLight, clearLights, getLightStyleForGid } from "./lighting.js";
import { spawnHuman } from "../entities/humanAI.js";
import { spawnGuardRobot } from "../entities/robotAI.js";
import { spawnHomelessGuy } from "../entities/homelessGuy.js";
import { isLoaded, saveCheckpoint } from "./memory.js";
import { GAME_CONFIG } from "../config/gameConfig.js";
import {
    SPRITEFUSION_SPRITE,
    buildCollisionRows,
    buildRoomRectsFromCollision,
    buildRoomRectsFromRegionLayer,
    findFloorSpawnCell,
    findNearestPassableCell,
    getLayer,
    getLayerZ,
    getMarkerCells,
    getRenderableLayers,
    getSpriteFusionMap,
    getTileGid,
    hasWalkableLayer,
    tileGidToFrame,
} from "./spritefusionMap.js";
import { setRooms, clearRooms, getRoomAt, getNearestRoom } from "./rooms.js";
import { clearScentMarks } from "./sniff.js";
import { clearAlarmLockdowns } from "./alarmLockdown.js";
import { isDebugModeVisible } from "./debugState.js";
import { clearCutsceneLocks } from "./cutsceneState.js";
import { createRamLatchPuzzle, collectRamLatchFragmentById } from "../entities/ramLatch.js";
import { queueTextbox, isTextboxOpen } from "../ui/textboxUI.js";

/**
 * Facility level — SpriteFusion map + KAPLAY path grid + room fog
 */

const TILE_W = GAME_CONFIG.level.tileWidth;
const TILE_H = GAME_CONFIG.level.tileHeight;
const CLUE_LAYER_NAMES = ["clues", "clue"];
const LIGHT_LAYER_NAMES = ["lights", "light"];
const SIGHT_BLOCK_LAYER_NAMES = ["decor"];
const DOOR_LAYER_KEYS = new Set(["door", "doors"]);
const AD_LAYER_KEYS = new Set(["ad", "ads"]);
const CLUE_LAYER_KEYS = new Set(["clue", "clues"]);
const TUNNEL_LAYER_KEYS = new Set(["tunnel", "tunnels"]);
const DOOR_NORMAL_OPACITY = 1;
const DOOR_PLAYER_UNDER_OPACITY = 0.35;
const AD_NORMAL_OPACITY = 1;
const AD_BLOCKED_OPACITY = 0;
const CLUE_HIDDEN_OPACITY = 0;
const CLUE_VISIBLE_OPACITY = 0.95;
const TUNNEL_VISIBLE_OPACITY = 1;
const TUNNEL_HIDDEN_OPACITY = 0;
const CHECKPOINT_ACTIVATION_RADIUS = 18;
const R2_SPAWN_ALARM_ROOM_ID = 2;
const R2_SPAWN_ALARM_DISTANCE_TILES = 5;
const POSITION_ANCHOR_LAYERS = ["anchor", "anchors", "origin", "positionanchor"];
const POSITION_ANCHOR_FALLBACK_CELL = { x: 12, y: 6 };
const INTERACTABLES = [
    {
        id: "crate_fragment",
        cells: [
            { x: 32, y: 50 },
        ],
        text: "The crate is taped shut. Pry it open and read the damp logs inside.",
        triggered: false,
        onTrigger: () => {
            queueTextbox({
                tag: "SYS // CRATE_LOG",
                channel: "R5_LATCH",
                text: "The crate opens to reveal soggy logs, and a flat keycard slips free.",
                variant: "system",
            });
            collectRamLatchFragmentById("crate");
            return true;
        },
    },
    {
        id: "toilet",
        cells: [
            { x: 35, y: 45 },
        ],
        text: "The toilet is cracked and damp. Something clinks inside.",
        triggered: false,
        onTrigger: () => {
            collectRamLatchFragmentById("toilet");
            return true;
        },
    },
];
const TILE_POSITIONS = {
    dogSpawn: { dx: -3, dy: -1 },
    checkpoints: [
        { dx: 20, dy: -1 },
        { dx: 33, dy: 14 },
        { dx: 53, dy: 18 },
        { dx: 72, dy: 19 },
    ],
    humanGuard: { dx: 66, dy: 18 },
    homelessGuy: { dx: 102, dy: -32 },
    patrolHumans: [
        { dx: 2, dy: -1 },
    ],
    circlingHumans: [
        { dx: 43, dy: 15 },
    ],
    suiteHumans: [
        { dx: 97, dy: 19 },
    ],
    guardRobots: [
        { dx: 33, dy: 50 },
        { dx: 65, dy: 16 },
        { dx: 68, dy: 15 },
        { dx: 65, dy: 21 },
        { dx: 77, dy: 57 },
        { dx: 80, dy: 58 },
        { dx: 76, dy: 58 },
        { dx: 60, dy: 19 },
        { dx: 93, dy: 18 },
        { dx: 62, dy: 22 },
        { dx: 61, dy: 15 },
        { dx: -10, dy: -2 },
        { dx: 46, dy: 16 },
    ],
};

let collisionRows = [];
let sightBlockRows = [];
let currentLevel = null;
let tunnelGroupsByCell = new Map();
let adLayerTiles = [];
let clueLayerTiles = [];
let tunnelLayerTiles = [];
let doorOverlayTiles = [];
let lastAdBlockerLoaded = null;
let lastSniffLoaded = null;
let lastTunnelGroupId = undefined;

export function getLevel() {
    return currentLevel;
}

function ensureCollisionRows() {
    if (collisionRows.length > 0) return;
    collisionRows = buildCollisionRows(getSpriteFusionMap());
}

function cellToWorldCenter(cell) {
    return vec2(cell.x * TILE_W + TILE_W / 2, cell.y * TILE_H + TILE_H / 2);
}

function getPositionAnchorCell(map) {
    return getMarkerCells(map, POSITION_ANCHOR_LAYERS)[0] || POSITION_ANCHOR_FALLBACK_CELL;
}

export function getPositionAnchorDebugCell() {
    return getPositionAnchorCell(getSpriteFusionMap());
}

function worldCellByAnchorOffset(map, offset) {
    const anchorCell = getPositionAnchorCell(map);
    const targetX = anchorCell.x + offset.dx;
    const targetY = anchorCell.y + offset.dy;
    return cellToWorldCenter(findNearestPassableCell(collisionRows, targetX, targetY));
}

function r2SpawnProximityAlarmOptions(posVec) {
    const room = getRoomAt(posVec);
    if (room?.id !== R2_SPAWN_ALARM_ROOM_ID) return {};

    return {
        proximityAlarm: {
            roomId: R2_SPAWN_ALARM_ROOM_ID,
            origin: getSpawnPos(),
            distance: R2_SPAWN_ALARM_DISTANCE_TILES * TILE_W,
        },
    };
}

export function isWalkableWorldPos(worldPos) {
    if (!worldPos) return false;
    ensureCollisionRows();
    const x = Math.floor(worldPos.x / TILE_W);
    const y = Math.floor(worldPos.y / TILE_H);
    if (y < 0 || y >= collisionRows.length) return false;
    if (x < 0 || x >= collisionRows[y].length) return false;
    return collisionRows[y][x] === ".";
}

/**
 * Move `from` by `delta` without leaving walkable tiles.
 * Tries full move, then X-only / Y-only slides. Used by knockback / lunges.
 */
export function movePosOnWalkable(from, delta) {
    if (!from || !delta) {
        return { pos: from, moved: false, hitWall: false };
    }

    const full = from.add(delta);
    if (isWalkableWorldPos(full)) {
        return { pos: full, moved: true, hitWall: false };
    }

    let pos = from;
    let moved = false;
    let hitWall = true;

    if (delta.x !== 0) {
        const onlyX = vec2(from.x + delta.x, from.y);
        if (isWalkableWorldPos(onlyX)) {
            pos = onlyX;
            moved = true;
        }
    }

    if (delta.y !== 0) {
        const onlyY = vec2(pos.x, from.y + delta.y);
        if (isWalkableWorldPos(onlyY)) {
            pos = onlyY;
            moved = true;
        }
    }

    return { pos, moved, hitWall };
}

export function resolveWalkablePos(worldPos) {
    if (!worldPos) return getSpawnPos();
    ensureCollisionRows();
    const x = Math.floor(worldPos.x / TILE_W);
    const y = Math.floor(worldPos.y / TILE_H);
    return cellToWorldCenter(findNearestPassableCell(collisionRows, x, y));
}

function buildSightBlockRows(map) {
    const decor = getLayer(map, SIGHT_BLOCK_LAYER_NAMES);

    return Array.from({ length: map.height }, (_, y) => {
        let row = "";
        for (let x = 0; x < map.width; x++) {
            const blocksByCollision = collisionRows[y]?.[x] !== ".";
            const blocksByDecor = decor ? getTileGid(decor, x, y) > 0 : false;
            row += blocksByCollision || blocksByDecor ? "#" : ".";
        }
        return row;
    });
}

function isSightBlockedCell(x, y) {
    if (y < 0 || y >= sightBlockRows.length) return true;
    if (x < 0 || x >= sightBlockRows[y].length) return true;
    return sightBlockRows[y][x] === "#";
}

export function hasLineOfSightWorld(fromPos, toPos) {
    if (!fromPos || !toPos) return false;
    if (sightBlockRows.length === 0) {
        sightBlockRows = buildSightBlockRows(getSpriteFusionMap());
    }

    const from = vec2(fromPos.x, fromPos.y);
    const to = vec2(toPos.x, toPos.y);
    const delta = to.sub(from);
    const dist = delta.len();
    if (dist <= 0.1) return true;

    const steps = Math.max(1, Math.ceil(dist / 8));
    for (let i = 1; i < steps; i++) {
        const p = from.add(delta.scale(i / steps));
        const cellX = Math.floor(p.x / TILE_W);
        const cellY = Math.floor(p.y / TILE_H);
        if (isSightBlockedCell(cellX, cellY)) return false;
    }

    return true;
}

export function getSightEndPoint(fromPos, angleDeg, maxDistance) {
    if (!fromPos) return null;
    if (sightBlockRows.length === 0) {
        sightBlockRows = buildSightBlockRows(getSpriteFusionMap());
    }

    const start = vec2(fromPos.x, fromPos.y);
    const dir = Vec2.fromAngle(angleDeg);
    const dist = Math.max(0, maxDistance || 0);
    const steps = Math.max(1, Math.ceil(dist / 8));

    let lastOpen = start;
    for (let i = 1; i <= steps; i++) {
        const stepDist = Math.min(dist, i * 8);
        const p = start.add(dir.scale(stepDist));
        const cellX = Math.floor(p.x / TILE_W);
        const cellY = Math.floor(p.y / TILE_H);
        if (isSightBlockedCell(cellX, cellY)) {
            return lastOpen;
        }
        lastOpen = p;
    }

    return start.add(dir.scale(dist));
}

function worldToCell(worldPos) {
    return {
        x: Math.floor(worldPos.x / TILE_W),
        y: Math.floor(worldPos.y / TILE_H),
    };
}

function collectWalkableEdgeCells() {
    ensureCollisionRows();
    const rows = collisionRows.length;
    const cols = rows > 0 ? collisionRows[0].length : 0;
    const cells = [];

    for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
            if (collisionRows[y][x] !== ".") continue;
            const touchesEdge = x === 0 || y === 0 || x === cols - 1 || y === rows - 1;
            const nearEdge = x <= 1 || y <= 1 || x >= cols - 2 || y >= rows - 2;
            if (touchesEdge || nearEdge) {
                cells.push({ x, y });
            }
        }
    }

    return cells;
}

export function getFleeTargetAwayFrom(worldPos, threatPos) {
    const fallback = isWalkableWorldPos(worldPos) ? worldPos : getSpawnPos();
    const edgeCells = collectWalkableEdgeCells();
    if (edgeCells.length === 0) return fallback;

    const fromCell = worldToCell(worldPos);
    const threatCell = threatPos ? worldToCell(threatPos) : fromCell;
    let best = edgeCells[0];
    let bestScore = -Infinity;

    for (const cell of edgeCells) {
        const threatDx = cell.x - threatCell.x;
        const threatDy = cell.y - threatCell.y;
        const selfDx = cell.x - fromCell.x;
        const selfDy = cell.y - fromCell.y;
        const threatDist = threatDx * threatDx + threatDy * threatDy;
        const selfDist = selfDx * selfDx + selfDy * selfDy;
        const score = threatDist - selfDist * 0.18;
        if (score > bestScore) {
            bestScore = score;
            best = cell;
        }
    }

    return cellToWorldCenter(best);
}

/** Snap invalid/outdated save positions onto the nearest walkable floor cell. */
export function resolvePlayablePos(worldPos) {
    if (isWalkableWorldPos(worldPos)) {
        return vec2(worldPos.x, worldPos.y);
    }
    return getSpawnPos();
}

function addCheckpointAt(centerPos, roomId = 1) {
    const chk = add([
        circle(6),
        pos(centerPos),
        anchor("center"),
        color(0, 220, 255),
        opacity(0),
        z(5),
        "checkpoint_tile",
        {
            triggered: false,
            roomId,
        },
    ]);

    function activateCheckpoint() {
        if (chk.triggered) return;
        chk.triggered = true;
        chk.color = rgb(0, 255, 180);
        saveCheckpoint(centerPos, roomId);
    }

    chk.onUpdate(() => {
        chk.opacity = isDebugModeVisible() ? 0.6 : 0;
        if (chk.triggered) return;
        const player = get("player")[0];
        if (
            player?.exists?.()
            && isLoaded("core_locomotion")
            && isLoaded("core_vision")
            && player.pos.dist(centerPos) <= CHECKPOINT_ACTIVATION_RADIUS
        ) {
            activateCheckpoint();
        }
    });
}

export function getSpawnPos() {
    const map = getSpriteFusionMap();
    ensureCollisionRows();
    return worldCellByAnchorOffset(map, TILE_POSITIONS.dogSpawn);
}

function cellKey(x, y) {
    return `${x},${y}`;
}

function buildLayerTileGroups(layer) {
    const visited = Array.from({ length: layer.height }, () =>
        Array(layer.width).fill(false),
    );
    const groupsByCell = new Map();
    let nextGroupId = 1;

    for (let y = 0; y < layer.height; y++) {
        for (let x = 0; x < layer.width; x++) {
            if (visited[y][x] || getTileGid(layer, x, y) === 0) continue;

            const groupId = nextGroupId++;
            const stack = [{ x, y }];
            visited[y][x] = true;

            while (stack.length > 0) {
                const cell = stack.pop();
                groupsByCell.set(cellKey(cell.x, cell.y), groupId);

                for (const n of [
                    { x: cell.x + 1, y: cell.y },
                    { x: cell.x - 1, y: cell.y },
                    { x: cell.x, y: cell.y + 1 },
                    { x: cell.x, y: cell.y - 1 },
                ]) {
                    if (n.x < 0 || n.y < 0 || n.x >= layer.width || n.y >= layer.height) continue;
                    if (visited[n.y][n.x] || getTileGid(layer, n.x, n.y) === 0) continue;
                    visited[n.y][n.x] = true;
                    stack.push(n);
                }
            }
        }
    }

    return groupsByCell;
}

function getNearbyTunnelGroupId(cellX, cellY) {
    return tunnelGroupsByCell.get(cellKey(cellX, cellY)) ?? null;
}

export function isWorldPosInTunnel(worldPos) {
    if (!worldPos) return false;
    const cellX = Math.floor(worldPos.x / TILE_W);
    const cellY = Math.floor(worldPos.y / TILE_H);
    return getNearbyTunnelGroupId(cellX, cellY) != null;
}

function renderMapTiles(map) {
    const tileScaleX = TILE_W / map.tileWidth;
    const tileScaleY = TILE_H / map.tileHeight;
    tunnelGroupsByCell = new Map();

    getRenderableLayers(map).forEach((layer) => {
        const layerZ = getLayerZ(layer.name);
        const isDoorLayer = DOOR_LAYER_KEYS.has(layer.key);
        const isAdLayer = AD_LAYER_KEYS.has(layer.key);
        const isClueLayer = CLUE_LAYER_KEYS.has(layer.key);
        const isTunnelLayer = TUNNEL_LAYER_KEYS.has(layer.key);
        const layerGroups = isTunnelLayer ? buildLayerTileGroups(layer) : null;
        if (isTunnelLayer) {
            tunnelGroupsByCell = layerGroups;
        }

        for (let y = 0; y < layer.height; y++) {
            for (let x = 0; x < layer.width; x++) {
                const gid = getTileGid(layer, x, y);
                if (gid === 0) continue;
                const tunnelGroupId = layerGroups?.get(cellKey(x, y)) ?? null;

                add([
                    sprite(SPRITEFUSION_SPRITE, {
                        frame: tileGidToFrame(map, gid),
                    }),
                    pos(x * TILE_W, y * TILE_H),
                    anchor("topleft"),
                    scale(tileScaleX, tileScaleY),
                    z(layerZ),
                    ...(isDoorLayer
                        ? [opacity(DOOR_NORMAL_OPACITY), "door_overlay", { tileX: x, tileY: y }]
                        : []),
                    ...(isAdLayer
                        ? [opacity(AD_NORMAL_OPACITY), "ad_layer_tile"]
                        : []),
                    ...(isClueLayer
                        ? [opacity(isLoaded("action_sniff") ? CLUE_VISIBLE_OPACITY : CLUE_HIDDEN_OPACITY), "clue_layer_tile"]
                        : []),
                    ...(isTunnelLayer
                        ? [
                            opacity(TUNNEL_HIDDEN_OPACITY),
                            "tunnel_layer_tile",
                            { tileX: x, tileY: y, tunnelGroupId },
                        ]
                        : []),
                ]);
            }
        }
    });
}

function refreshLayerTileCaches() {
    adLayerTiles = get("ad_layer_tile");
    clueLayerTiles = get("clue_layer_tile");
    tunnelLayerTiles = get("tunnel_layer_tile");
    doorOverlayTiles = get("door_overlay");
    lastAdBlockerLoaded = null;
    lastSniffLoaded = null;
    lastTunnelGroupId = undefined;
}

function updateAdLayerVisibility() {
    const adBlockerLoaded = isLoaded("utility_ad_blocker");
    if (adBlockerLoaded === lastAdBlockerLoaded) return;
    lastAdBlockerLoaded = adBlockerLoaded;

    const targetOpacity = adBlockerLoaded
        ? AD_BLOCKED_OPACITY
        : AD_NORMAL_OPACITY;

    for (const adTile of adLayerTiles) {
        adTile.opacity = targetOpacity;
    }
}

function updateClueLayerVisibility() {
    const sniffLoaded = isLoaded("action_sniff");
    const targetOpacity = sniffLoaded
        ? CLUE_VISIBLE_OPACITY
        : CLUE_HIDDEN_OPACITY;

    if (sniffLoaded === lastSniffLoaded) {
        let settled = true;
        for (const clueTile of clueLayerTiles) {
            if (Math.abs(clueTile.opacity - targetOpacity) > 0.01) {
                settled = false;
                break;
            }
        }
        if (settled) return;
    }
    lastSniffLoaded = sniffLoaded;

    for (const clueTile of clueLayerTiles) {
        clueTile.opacity = Math.abs(clueTile.opacity - targetOpacity) < 0.01
            ? targetOpacity
            : lerp(clueTile.opacity, targetOpacity, 0.35);
    }
}

function createInteractableHighlights() {
    for (const interactable of INTERACTABLES) {
        for (const cell of interactable.cells) {
            add([
                rect(TILE_W + 2, TILE_H + 2),
                pos(cell.x * TILE_W - 1, cell.y * TILE_H - 1),
                anchor("topleft"),
                color(160, 230, 255),
                outline(2, rgb(180, 255, 255)),
                opacity(0.18),
                blend("screen"),
                z(95),
                "interactive_highlight",
                { tileX: cell.x, tileY: cell.y, baseOpacity: 0.18 },
            ]);
        }
    }
}

function updateInteractableHighlights() {
    const highlights = get("interactive_highlight");
    if (!highlights || highlights.length === 0) return;
    const t = time();
    for (const highlight of highlights) {
        highlight.opacity = highlight.baseOpacity + 0.08 + Math.sin(t * 6 + highlight.tileX * 0.8 + highlight.tileY * 1.3) * 0.1;
    }
}

function updateInteractiveProximity() {
    const player = get("player")[0];
    if (!player || isTextboxOpen()) return;
    const playerPos = player.pos;

    for (const interactable of INTERACTABLES) {
        if (interactable.triggered) continue;
        const isClose = interactable.cells.some((cell) => {
            const cellCenter = vec2(cell.x * TILE_W + TILE_W / 2, cell.y * TILE_H + TILE_H / 2);
            return playerPos.dist(cellCenter) <= TILE_W;
        });
        if (!isClose) continue;

        let suppressDefaultText = false;
        if (typeof interactable.onTrigger === "function") {
            suppressDefaultText = interactable.onTrigger() === true;
        }

        if (!suppressDefaultText) {
            queueTextbox({
                tag: `OBJ_SCAN // ${interactable.id.toUpperCase()}`,
                channel: "CH.01",
                text: interactable.text,
                variant: "system",
            });
        }

        interactable.triggered = true;
        break;
    }
}

function updateTunnelVisibility() {
    const player = get("player")[0];
    if (!player || tunnelLayerTiles.length === 0) return;

    const playerCellX = Math.floor(player.pos.x / TILE_W);
    const playerCellY = Math.floor(player.pos.y / TILE_H);
    const activeGroupId = getNearbyTunnelGroupId(playerCellX, playerCellY);
    if (activeGroupId === lastTunnelGroupId) {
        let settled = true;
        for (const tunnelTile of tunnelLayerTiles) {
            const targetOpacity = activeGroupId != null && tunnelTile.tunnelGroupId === activeGroupId
                ? TUNNEL_VISIBLE_OPACITY
                : TUNNEL_HIDDEN_OPACITY;
            if (Math.abs(tunnelTile.opacity - targetOpacity) > 0.01) {
                settled = false;
                break;
            }
        }
        if (settled) return;
    }
    lastTunnelGroupId = activeGroupId;

    for (const tunnelTile of tunnelLayerTiles) {
        const targetOpacity = activeGroupId != null && tunnelTile.tunnelGroupId === activeGroupId
            ? TUNNEL_VISIBLE_OPACITY
            : TUNNEL_HIDDEN_OPACITY;

        tunnelTile.opacity = Math.abs(tunnelTile.opacity - targetOpacity) < 0.01
            ? targetOpacity
            : lerp(tunnelTile.opacity, targetOpacity, 0.35);
    }
}

function updateDoorOpacity() {
    const player = get("player")[0];

    for (const door of doorOverlayTiles) {
        const isPlayerUnderDoor = player
            && player.pos.x >= door.tileX * TILE_W
            && player.pos.x < (door.tileX + 1) * TILE_W
            && player.pos.y >= door.tileY * TILE_H
            && player.pos.y < (door.tileY + 1) * TILE_H;

        const targetOpacity = isPlayerUnderDoor
            ? DOOR_PLAYER_UNDER_OPACITY
            : DOOR_NORMAL_OPACITY;

        door.opacity = lerp(door.opacity, targetOpacity, 0.25);
    }
}

function isPassableCell(rows, x, y) {
    if (y < 0 || y >= rows.length) return false;
    if (x < 0 || x >= rows[y].length) return false;
    return rows[y][x] === ".";
}

function isBoundaryWallCell(rows, x, y) {
    if (rows[y][x] !== "#") return false;
    return (
        isPassableCell(rows, x + 1, y) ||
        isPassableCell(rows, x - 1, y) ||
        isPassableCell(rows, x, y + 1) ||
        isPassableCell(rows, x, y - 1)
    );
}

function addCollisionWalls(rows) {
    for (let y = 0; y < rows.length; y++) {
        let x = 0;
        while (x < rows[y].length) {
            if (!isBoundaryWallCell(rows, x, y)) {
                x++;
                continue;
            }

            const runStart = x;
            while (x < rows[y].length && isBoundaryWallCell(rows, x, y)) {
                x++;
            }
            const runLength = x - runStart;

            add([
                rect(runLength * TILE_W, TILE_H),
                pos(runStart * TILE_W, y * TILE_H),
                opacity(0),
                area(),
                body({ isStatic: true }),
                "wall",
            ]);
        }
    }
}

/** Invisible KAPLAY level grid for robot getPath (no physics bodies). */
function createPathLevel(rows) {
    return addLevel(rows, {
        tileWidth: TILE_W,
        tileHeight: TILE_H,
        pos: vec2(0, 0),
        tiles: {
            "#": () => [
                tile({ isObstacle: true }),
            ],
            ".": () => [],
        },
    });
}

function addMapLights(map) {
    const lightLayer = getLayer(map, LIGHT_LAYER_NAMES);
    if (!lightLayer) return;

    const placed = [];
    for (let y = 0; y < lightLayer.height; y++) {
        for (let x = 0; x < lightLayer.width; x++) {
            const gid = getTileGid(lightLayer, x, y);
            if (gid === 0) continue;

            const posVec = vec2(x * TILE_W + TILE_W / 2, y * TILE_H + TILE_H / 2);
            const room = getRoomAt(posVec) || getNearestRoom(posVec);
            const style = getLightStyleForGid(gid);

            setLight(`map_light_${x}_${y}_${gid}`, {
                pos: posVec,
                radius: style.radius,
                color: style.color,
                intensity: style.intensity,
                flicker: style.flicker,
                pulse: style.pulse,
                roomId: room ? room.id : null,
                gid,
                alarm: style.alarm,
                alarmIntensity: style.alarmIntensity,
                baseIntensity: style.intensity,
            });
            placed.push(`(${x},${y}) gid=${gid}`);
        }
    }

    console.info(`[lighting] Light-layer emitters (${placed.length}):`, placed.join(" | "));
}

function addClueLights(map) {
    const clueLayer = getLayer(map, CLUE_LAYER_NAMES);
    if (!clueLayer) return;

    const placed = [];
    for (let y = 0; y < clueLayer.height; y++) {
        for (let x = 0; x < clueLayer.width; x++) {
            const gid = getTileGid(clueLayer, x, y);
            if (gid === 0) continue;

            const posVec = vec2(x * TILE_W + TILE_W / 2, y * TILE_H + TILE_H / 2);
            const room = getRoomAt(posVec) || getNearestRoom(posVec);

            setLight(`clue_light_${x}_${y}_${gid}`, {
                pos: posVec,
                radius: 76,
                color: [255, 255, 255],
                intensity: 0.52,
                pulse: true,
                flicker: false,
                roomId: room ? room.id : null,
                clue: true,
            });
            placed.push(`(${x},${y}) gid=${gid}`);
        }
    }

    console.info(`[lighting] Clue-layer emitters (${placed.length}):`, placed.join(" | "));
}

function spawnEntities(map) {
    const humanPos = worldCellByAnchorOffset(map, TILE_POSITIONS.humanGuard);
    spawnHomelessGuy(worldCellByAnchorOffset(map, TILE_POSITIONS.homelessGuy), {
        hasLineOfSight: hasLineOfSightWorld,
        sightRadius: 90,
    });

    spawnHuman({
        pos: humanPos,
        facingAngle: 180,
        ...r2SpawnProximityAlarmOptions(humanPos),
        patrol: {
            loop: true,
            speed: 40,
            turnSpeed: 220,
            steps: [
                { x: humanPos.x, y: humanPos.y, face: 180, wait: 1.5 },
                { x: humanPos.x + 48, y: humanPos.y, face: 0, wait: 1.0, rotate: "cw" },
                { x: humanPos.x + 48, y: humanPos.y + 32, face: 90, wait: 1.0 },
                { x: humanPos.x, y: humanPos.y + 32, face: 180, wait: 0.8 },
            ],
        },
    });
    for (const humanOffset of TILE_POSITIONS.patrolHumans) {
        const patrolPos = worldCellByAnchorOffset(map, humanOffset);
        spawnHuman({
            pos: patrolPos,
            facingAngle: 0,
            ...r2SpawnProximityAlarmOptions(patrolPos),
            patrol: {
                loop: true,
                speed: 36,
                turnSpeed: 220,
                steps: [
                    { x: patrolPos.x, y: patrolPos.y, face: 0, wait: 0.8 },
                    { x: patrolPos.x + 32, y: patrolPos.y, face: 0, wait: 0.8 },
                    { x: patrolPos.x + 32, y: patrolPos.y + 16, face: 90, wait: 0.6 },
                    { x: patrolPos.x, y: patrolPos.y + 16, face: 180, wait: 0.8 },
                ],
            },
        });
    }
    for (const humanOffset of TILE_POSITIONS.circlingHumans) {
        const patrolPos = worldCellByAnchorOffset(map, humanOffset);
        spawnHuman({
            pos: patrolPos,
            facingAngle: 0,
            ...r2SpawnProximityAlarmOptions(patrolPos),
            patrol: {
                loop: true,
                speed: 36,
                turnSpeed: 220,
                steps: [
                    { x: patrolPos.x, y: patrolPos.y - 32, face: 270, wait: 0.35 },
                    { x: patrolPos.x + 32, y: patrolPos.y, face: 0, wait: 0.35 },
                    { x: patrolPos.x, y: patrolPos.y + 32, face: 90, wait: 0.35 },
                    { x: patrolPos.x - 32, y: patrolPos.y, face: 180, wait: 0.35 },
                ],
            },
        });
    }
    for (const humanOffset of TILE_POSITIONS.suiteHumans) {
        const suitePos = worldCellByAnchorOffset(map, humanOffset);
        spawnHuman({
            pos: suitePos,
            facingAngle: 180,
            spriteSet: "suite",
            visionConeAngle: 360,
            ...r2SpawnProximityAlarmOptions(suitePos),
            ramDropType: "upgrade",
            ramUnlockModuleIds: ["action_sniff", "utility_ad_blocker"],
        });
    }
    for (const robotOffset of TILE_POSITIONS.guardRobots) {
        spawnGuardRobot(worldCellByAnchorOffset(map, robotOffset), false);
    }
}

export function createLevel() {
    const map = getSpriteFusionMap();

    clearLights();
    clearScentMarks();
    clearRooms();
    clearAlarmLockdowns();
    clearCutsceneLocks();
    currentLevel = null;

    collisionRows = buildCollisionRows(map);
    sightBlockRows = buildSightBlockRows(map);

    if (!hasWalkableLayer(map)) {
        console.warn("SpriteFusion map has no `walkable` layer; no map tiles will be passable.");
    }

    const regionRooms = buildRoomRectsFromRegionLayer(map);
    const roomDefs = regionRooms && regionRooms.length > 0
        ? regionRooms
        : buildRoomRectsFromCollision(collisionRows);
    setRooms(roomDefs);

    renderMapTiles(map);
    refreshLayerTileCaches();
    updateAdLayerVisibility();
    updateClueLayerVisibility();
    updateTunnelVisibility();
    currentLevel = createPathLevel(collisionRows);
    addCollisionWalls(collisionRows);
    addMapLights(map);
    addClueLights(map);

    createInteractableHighlights();
    createRamLatchPuzzle(map);

    for (const checkpointOffset of TILE_POSITIONS.checkpoints) {
        const posVec = worldCellByAnchorOffset(map, checkpointOffset);
        const room = getRoomAt(posVec);
        addCheckpointAt(posVec, room ? room.id : 1);
    }

    const markerCheckpoints = getMarkerCells(map, ["checkpoints", "checkpoint", "spawn"]);
    if (markerCheckpoints.length > 0) {
        for (const cell of markerCheckpoints) {
            const posVec = cellToWorldCenter(cell);
            const room = getRoomAt(posVec);
            addCheckpointAt(posVec, room ? room.id : 1);
        }
    } else {
        const spawn = getSpawnPos();
        const spawnRoom = getRoomAt(spawn);
        addCheckpointAt(spawn, spawnRoom ? spawnRoom.id : 1);
    }

    spawnEntities(map);
    onUpdate(updateDoorOpacity);
    onUpdate(updateAdLayerVisibility);
    onUpdate(updateClueLayerVisibility);
    onUpdate(updateTunnelVisibility);
    onUpdate(updateInteractableHighlights);
    onUpdate(updateInteractiveProximity);

    return currentLevel;
}
